import React from 'react'
import i18n from 'i18n'

import { Descriptions, Card, Input, InputNumber, Switch, Select, ColorPicker } from 'antd'
import type { ColorPickerProps } from 'antd'

// [사용 안 함] 아래 HTMLEditorWrapper 주석처리에 따라 미사용 상태이므로 import 도 비활성화한다.
// 사용하게 되는 경우 ngwEditor를 적용하여 사용하도록 할 것.
// import { Editor } from '@tinymce/tinymce-react'

import classNames from 'classnames'
import chevronDown24 from 'assets/images/chevron-down-24.svg'

import styles0 from './style.module.scss'

import moreIcon from 'assets/images/toolbar-more-24.svg'

interface GetTitleElemOptions {
  title?: string
  titleKey?: string
  children?: React.ReactNode
  extra?: React.ReactNode
}

export function getTitleElem(options: GetTitleElemOptions): React.ReactElement | null {
  const { title, titleKey, children, extra /*, styles */ } = options

  /* i18next-extract-disable-next-line */
  const titleText = titleKey != null ? i18n.t(titleKey) : title

  if (children != null || title != null) {
    return (
      <Descriptions
        className={classNames(styles0.propsUI, 'propsUI')}
        title={titleText}
        size="small"
        column={1}
        bordered
        extra={extra}
      >
        {children}
      </Descriptions>
    )
  }

  return null
}

interface ItemInfo {
  attrs?: Record<string, any>
  parent?: any
  children?: Record<string, ItemInfo>
  wrapper: any
  inputAttrs?: Record<string, any>
  makeLabel: () => void
  useCheck: () => boolean
  getElement: () => React.ReactElement | null
}

interface ArrayWrapperOptions {
  keyStr?: string
  [key: string]: any
}

interface ReactObjBase {
  onPropsChange?: (key: string, value: any) => void
  forceUpdate?: () => void
  [key: string]: any
}

class ArrayWrapper {
  pKey: string
  items: ItemInfo[]
  value: any[]
  arrayInfo: Record<string, ItemInfo>
  itemOptions: Record<string, any>
  keyStr?: string

  constructor(
    pKey: string,
    info: ItemInfo,
    items: Record<string, any>,
    options: ArrayWrapperOptions,
    _styles: any,
    _reactObj: ReactObjBase,
  ) {
    this.pKey = pKey
    this.items = []
    this.value = []
    this.arrayInfo = {}
    this.itemOptions = {
      ...items,
    }
    this.keyStr = options.keyStr
  }

  setValue(value: any[]): void {
    console.log('array - setValue -', value)
    if (value != null) {
      this.value = [...value]
      this.arrayInfo = {} // 항상 새로 만들어야 함.
      this.items = this.value.map((item, idx) => {
        const key = this.keyStr != null ? item[this.keyStr] : Date.now()
        const opts = {
          ...this.itemOptions,
          label: `item${idx}`,
        }
        const itemInfo = getItemInfo(this.arrayInfo, `item${key}${idx}`, opts, null, null)
        itemInfo.wrapper.setValue(item)
        return itemInfo
      })
    }
  }

  getValue(): any[] | null {
    if (this.items != null) {
      const ret = this.items.map((itemInfo) => {
        return itemInfo.wrapper.getValue()
      })
      return ret
    }
    return null
  }

  addItem(item: any): void {
    const idx = this.items.length
    const key = this.keyStr != null ? item[this.keyStr] : Date.now()
    const opts = {
      ...this.itemOptions,
      label: `item${idx}`,
    }
    const itemInfo = getItemInfo(this.arrayInfo, `item${key}${idx}`, opts, null, null)

    this.items.push(itemInfo)
    itemInfo.wrapper.setValue(item)
  }

  getItems(): ItemInfo[] {
    return this.items
  }

  getItem(idx: number): ItemInfo {
    return this.items[idx]
  }

  removeItem(item: ItemInfo): void {
    const pos = this.items.indexOf(item)
    this.removeItemByIdx(pos)
  }

  removeItemByIdx(itemIdx: number): void {
    if (itemIdx >= 0) {
      this.items.splice(itemIdx, 1)
    }
  }

  getElement(): React.ReactElement {
    const childElems = this.items.map((itemInfo /* , idx */) => {
      return itemInfo.getElement()
    })

    const element = (
      <Descriptions
        className={classNames('itemDetail', 'subTitle')}
        size="small"
        column={1}
        bordered
        key={this.pKey}
      >
        {childElems}
      </Descriptions>
    )

    return element
  }
}

class JSONWrapper {
  pKey: string
  info: Record<string, ItemInfo>
  viewInfo: Record<string, ItemInfo>
  vGroupList: ViewGroup[]

  constructor(
    pKey: string,
    info: ItemInfo,
    children: Record<string, any>,
    options: Record<string, any>,
    styles: any,
    reactObj: ReactObjBase,
  ) {
    this.pKey = pKey
    info.children = {}
    this.info = info.children
    this.viewInfo = {}
    this.vGroupList = []

    Object.entries(children).forEach(([key, opts]) => {
      if (this.info[key] == null) {
        this.info[key] = getItemInfo(
          info.children as Record<string, ItemInfo>,
          key,
          opts,
          styles,
          reactObj,
        )
        if (opts.vGroup != null) {
          if (this.vGroupList.indexOf(opts.vGroup) < 0) {
            this.vGroupList.push(opts.vGroup)
          }
          opts.vGroup.addItemInfo(key, this.info[key])
        } else {
          this.viewInfo[key] = this.info[key]
        }
      }
    })
  }

  setValue(value: any): void {
    Object.entries(this.info).forEach(([key, childInfo]) => {
      const newValue = value != null ? value[key] : null
      childInfo.wrapper.setValue(newValue)
    })
  }

  getValue(): Record<string, any> {
    const ret: Record<string, any> = {}
    Object.entries(this.info).forEach(([key, childInfo]) => {
      const val = childInfo.wrapper.getValue()
      const { isCustom, useValue } = childInfo.wrapper
      if ((isCustom !== true || useValue === true) && this.info[key].useCheck() === true) {
        ret[key] = val
      }
    })
    return ret
  }

  getElement(): React.ReactElement {
    const childElems = Object.entries(this.viewInfo).map(([key /* , opts */]) => {
      return this.info[key].getElement()
    })

    const vGroupElems = this.vGroupList.map((vGroup) => {
      return vGroup.getElement()
    })

    const element = (
      <React.Fragment>
        <Descriptions
          className={classNames('childDetail', 'subTitle')}
          size="small"
          column={1}
          bordered
        >
          {childElems}
        </Descriptions>
        {vGroupElems}
      </React.Fragment>
    )

    return element
  }
}

interface SelectWrapperOptions {
  default?: any
  readOnly?: boolean
  options?: Record<string, any>
  values: Array<{ value?: any; label?: string | (() => string); labelKey?: string } | any>
}

class SelectWrapper {
  pKey: string
  ref: React.RefObject<any>
  isNumberType: boolean | null
  inputAttrs: Record<string, any>

  constructor(
    pKey: string,
    info: ItemInfo,
    inputAttrs: Record<string, any>,
    options: SelectWrapperOptions,
    styles: any,
    reactObj: ReactObjBase,
  ) {
    this.pKey = pKey
    this.ref = React.createRef()
    this.isNumberType = null

    console.log('new Select.')

    this.inputAttrs = {
      ref: this.ref,
      defaultValue: options.default,
      style: { width: '100% ' },
      ...inputAttrs,
      ...options.options,
    }

    this.inputAttrs.onChange = (e: any) => {
      if (inputAttrs != null && inputAttrs.onChange != null) {
        inputAttrs.onChange(e)
      }
      if (options.options != null && options.options.onChange != null) {
        options.options.onChange(e)
      }
      if (reactObj.onPropsChange != null) {
        reactObj.onPropsChange(this.pKey, e)
      }
    }

    if (options.readOnly === true) {
      this.inputAttrs.disabled = true
    }
  }

  setValue(value: any): void {
    if (value !== undefined) {
      if (this.ref.current != null) {
        this.ref.current.value = value
      } else {
        this.inputAttrs.defaultValue = value
      }
    }
  }

  getValue(): any {
    let ret = null
    if (this.ref.current != null) {
      ret = this.ref.current.value
    }
    ret = this.inputAttrs.defaultValue

    if (this.isNumberType === true) {
      ret = Number(ret)
    }

    return ret
  }

  getElement(options: SelectWrapperOptions): React.ReactElement {
    const opts = options.values.map((item) => {
      let opt = null
      if (item.value != null) {
        let labelObj = item.label
        if (item.label != null && typeof item.label === 'function') {
          labelObj = item.label()
        }

        const type = typeof item.value
        this.isNumberType = type === 'number'

        /* i18next-extract-disable-next-line */
        const labelText = item.labelKey != null ? i18n.t(item.labelKey) : labelObj
        opt = {
          value: item.value,
          label: labelText || item.value,
        }
      } else {
        opt = {
          value: item,
          label: item,
        }
      }

      return (
        <option value={opt.value} key={opt.value}>
          {opt.label}
        </option>
      )
    })

    const element = <select {...this.inputAttrs}>{opts}</select>

    return element
  }
}

interface CheckboxWrapperOptions {
  default?: boolean
  readOnly?: boolean
  options?: Record<string, any>
}

class CheckboxWrapper {
  pKey: string
  ref: React.RefObject<any>
  inputAttrs: Record<string, any>

  constructor(
    pKey: string,
    info: ItemInfo,
    inputAttrs: Record<string, any>,
    options: CheckboxWrapperOptions,
    styles: any,
    reactObj: ReactObjBase,
  ) {
    this.pKey = pKey
    this.ref = React.createRef()

    this.inputAttrs = {
      ref: this.ref,
      defaultChecked: options.default === true,
      ...inputAttrs,
      ...options.options,
    }

    this.inputAttrs.onChange = (e: any) => {
      if (inputAttrs != null && inputAttrs.onChange != null) {
        inputAttrs.onChange(e)
      }
      if (options.options != null && options.options.onChange != null) {
        options.options.onChange(e)
      }
      if (reactObj.onPropsChange != null) {
        reactObj.onPropsChange(this.pKey, e)
      }
    }

    if (options.readOnly === true) {
      this.inputAttrs.disabled = true
    }
  }

  setValue(value: boolean): void {
    if (value !== undefined) {
      if (this.ref.current != null) {
        this.ref.current.checked = value
      } else {
        this.inputAttrs.defaultChecked = value
      }
    }
  }

  getValue(): boolean {
    if (this.ref.current != null) {
      return this.ref.current.checked
    }
    return this.inputAttrs.defaultChecked
  }

  getElement(): React.ReactElement {
    const element = <input type="checkbox" {...this.inputAttrs} />

    return element
  }
}

interface AntdSelectWrapperOptions {
  default?: any
  readOnly?: boolean
  options?: Record<string, any>
  values: Array<{ value?: any; label?: string | (() => string); labelKey?: string } | any>
}

class AntdSelectWrapper {
  pKey: string
  value: any
  inputAttrs: Record<string, any>
  isNumberType?: boolean
  reactObj: ReactObjBase

  constructor(
    pKey: string,
    info: ItemInfo,
    inputAttrs: Record<string, any>,
    options: AntdSelectWrapperOptions,
    styles: any,
    reactObj: ReactObjBase,
  ) {
    this.pKey = pKey
    this.reactObj = reactObj
    this.value = options.default != null ? options.default : ''

    this.inputAttrs = {
      value: this.value,
      classNames: {
        root: classNames(styles?.select),
        popup: {
          root: classNames('contextMenu'),
        },
      },
      suffixIcon: <img src={chevronDown24} alt="chevron-down" />,
      ...inputAttrs,
      ...options.options,
    }

    this.inputAttrs.onChange = (e: any) => {
      this.value = e
      this.inputAttrs.value = this.value
      reactObj.forceUpdate?.()

      if (inputAttrs != null && inputAttrs.onChange != null) {
        inputAttrs.onChange(e)
      }
      if (options.options != null && options.options.onChange != null) {
        options.options.onChange(e)
      }
      if (reactObj.onPropsChange != null) {
        reactObj.onPropsChange(this.pKey, this.value)
      }
    }

    if (options.readOnly === true) {
      this.inputAttrs.disabled = true
    }
  }

  setValue(value: any): void {
    if (value !== undefined) {
      this.value = value
      this.inputAttrs.value = this.value
      this.reactObj.forceUpdate?.()
    }
  }

  getValue(): any {
    return this.value
  }

  getElement(options: AntdSelectWrapperOptions): React.ReactElement {
    const opts = options.values.map((item) => {
      let opt = null
      if (item.value != null) {
        let labelObj = item.label
        if (item.label != null && typeof item.label === 'function') {
          labelObj = item.label()
        }

        const type = typeof item.value
        this.isNumberType = type === 'number'

        /* i18next-extract-disable-next-line */
        const labelText = item.labelKey != null ? i18n.t(item.labelKey) : labelObj
        opt = {
          value: item.value,
          label: labelText || item.value,
        }
      } else {
        opt = {
          value: item,
          label: item,
        }
      }

      return opt
    })

    this.inputAttrs.options = opts

    const element = <Select {...this.inputAttrs} />

    return element
  }
}

interface AntdSwitchWrapperOptions {
  default?: boolean
  readOnly?: boolean
  options?: Record<string, any>
}

class AntdSwitchWrapper {
  pKey: string
  value: boolean
  inputAttrs: Record<string, any>
  reactObj: ReactObjBase

  constructor(
    pKey: string,
    info: ItemInfo,
    inputAttrs: Record<string, any>,
    options: AntdSwitchWrapperOptions,
    styles: any,
    reactObj: ReactObjBase,
  ) {
    this.pKey = pKey
    this.reactObj = reactObj
    this.value = options.default === true

    this.inputAttrs = {
      checked: this.value,
      ...inputAttrs,
      ...options.options,
    }

    this.inputAttrs.onChange = (e: boolean) => {
      this.value = e
      this.inputAttrs.checked = this.value
      reactObj.forceUpdate?.()

      if (inputAttrs != null && inputAttrs.onChange != null) {
        inputAttrs.onChange(e)
      }
      if (options.options != null && options.options.onChange != null) {
        options.options.onChange(e)
      }
      if (reactObj.onPropsChange != null) {
        reactObj.onPropsChange(this.pKey, e)
      }
    }

    if (options.readOnly === true) {
      this.inputAttrs.disabled = true
    }
  }

  setValue(value: boolean): void {
    if (value !== undefined) {
      this.value = value
      this.inputAttrs.checked = this.value
      this.reactObj.forceUpdate?.()
    }
  }

  getValue(): boolean {
    return this.value
  }

  getElement(): React.ReactElement {
    const element = (
      <div className={styles0.switchContainer}>
        <Switch {...this.inputAttrs} />
      </div>
    )

    return element
  }
}

interface AntdColorWrapperOptions {
  default?: string
  readOnly?: boolean
  options?: Record<string, any>
  showMore?: boolean
}

class AntdColorWrapper {
  pKey: string
  value: string
  inputAttrs: Record<string, any>
  reactObj: ReactObjBase
  options: AntdColorWrapperOptions

  constructor(
    pKey: string,
    info: ItemInfo,
    inputAttrs: Record<string, any>,
    options: AntdColorWrapperOptions,
    styles: any,
    reactObj: ReactObjBase,
  ) {
    this.pKey = pKey
    this.reactObj = reactObj
    this.options = options
    this.value = options.default != null ? options.default : '#000000'

    this.inputAttrs = {
      value: this.value,
      ...inputAttrs,
      ...options.options,
    }

    this.inputAttrs.onChange = (e: ColorPickerProps['value']) => {
      if (e && typeof e === 'object' && 'toHexString' in e) {
        this.value = (e as any).toHexString()
      } else {
        this.value = String(e)
      }
      this.inputAttrs.value = this.value
      reactObj.forceUpdate?.()

      if (inputAttrs != null && inputAttrs.onChange != null) {
        inputAttrs.onChange(e)
      }
      if (options.options != null && options.options.onChange != null) {
        options.options.onChange(e)
      }
      if (reactObj.onPropsChange != null) {
        reactObj.onPropsChange(this.pKey, this.value)
      }
    }

    if (options.readOnly === true) {
      this.inputAttrs.disabled = true
    }
  }

  setValue(value: string): void {
    if (value !== undefined) {
      this.value = value
      this.inputAttrs.value = this.value
      this.reactObj.forceUpdate?.()
    }
  }

  getValue(): string {
    return this.value.toUpperCase()
  }

  getElement(): React.ReactElement {
    const element = (
      <ColorPicker
        {...this.inputAttrs}
        showText={
          !this.options.showMore
            ? true
            : () => (
                <React.Fragment>
                  <div>{this.getValue()}</div>
                  <div className={styles0.colorPickerMore}>
                    <img src={moreIcon} alt="more" />
                  </div>
                </React.Fragment>
              )
        }
      />
    )

    return element
  }
}

interface AntdInputWrapperOptions {
  default?: string
  readOnly?: boolean
  options?: Record<string, any>
}

class AntdInputWrapper {
  pKey: string
  value: string
  inputAttrs: Record<string, any>
  reactObj: ReactObjBase

  constructor(
    pKey: string,
    info: ItemInfo,
    inputAttrs: Record<string, any>,
    options: AntdInputWrapperOptions,
    styles: any,
    reactObj: ReactObjBase,
  ) {
    this.pKey = pKey
    this.reactObj = reactObj
    this.value = options.default != null ? options.default : ''

    this.inputAttrs = {
      value: this.value,
      ...inputAttrs,
      ...options.options,
    }

    this.inputAttrs.onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      this.value = e.target.value || ''
      this.inputAttrs.value = this.value
      reactObj.forceUpdate?.()

      if (inputAttrs != null && inputAttrs.onChange != null) {
        inputAttrs.onChange(e)
      }
      if (options.options != null && options.options.onChange != null) {
        options.options.onChange(e)
      }
      if (reactObj.onPropsChange != null) {
        reactObj.onPropsChange(this.pKey, this.value)
      }
    }

    if (options.readOnly === true) {
      this.inputAttrs.disabled = true
    }
  }

  setValue(value: string): void {
    if (value !== undefined) {
      this.value = value
      this.inputAttrs.value = this.value
      this.reactObj.forceUpdate?.()
    }
  }

  getValue(): string {
    return this.value
  }

  getElement(): React.ReactElement {
    const element = <Input {...this.inputAttrs} />

    return element
  }
}

interface AntdNumberWrapperOptions {
  default?: number
  readOnly?: boolean
  options?: Record<string, any>
}

class AntdNumberWrapper {
  pKey: string
  value: number | string
  inputAttrs: Record<string, any>
  reactObj: ReactObjBase

  constructor(
    pKey: string,
    info: ItemInfo,
    inputAttrs: Record<string, any>,
    options: AntdNumberWrapperOptions,
    styles: any,
    reactObj: ReactObjBase,
  ) {
    this.pKey = pKey
    this.reactObj = reactObj
    this.value = options.default != null ? options.default : ''

    this.inputAttrs = {
      value: this.value,
      ...inputAttrs,
      ...options.options,
    }

    this.inputAttrs.onChange = (e: number | null) => {
      this.value = e ?? ''
      this.inputAttrs.value = this.value
      reactObj.forceUpdate?.()

      if (inputAttrs != null && inputAttrs.onChange != null) {
        inputAttrs.onChange(e)
      }
      if (options.options != null && options.options.onChange != null) {
        options.options.onChange(e)
      }
      if (reactObj.onPropsChange != null) {
        reactObj.onPropsChange(this.pKey, this.value)
      }
    }

    if (options.readOnly === true) {
      this.inputAttrs.disabled = true
    }
  }

  setValue(value: number): void {
    if (value !== undefined) {
      this.value = value
      this.inputAttrs.value = this.value
      this.reactObj.forceUpdate?.()
    }
  }

  getValue(): number | string {
    return this.value
  }

  getElement(): React.ReactElement {
    const element = <InputNumber {...this.inputAttrs} />

    return element
  }
}

/*
 * [사용 안 함] 아래 HTMLEditorWrapperOptions / HTMLEditorWrapper 는 현재 사용되지 않고 있어서
 * 주석처리함. 사용하게 되는 경우 ngwEditor를 적용하여 사용하도록 할 것.
 * (기존 구현은 @tinymce/tinymce-react + 내장 TinyMCE 5.10 기반이므로 그대로 재사용하지 말 것)
 *
interface HTMLEditorWrapperOptions {
  default?: string
  readOnly?: boolean
  options?: Record<string, any>
}

class HTMLEditorWrapper {
  pKey: string
  ref: React.MutableRefObject<any>
  editorId: string
  inputAttrs: Record<string, any>
  editorConfig?: any
  prevHeight?: number

  constructor(
    pKey: string,
    info: ItemInfo,
    inputAttrs: Record<string, any>,
    options: HTMLEditorWrapperOptions,
    _styles: any,
    _reactObj: ReactObjBase,
  ) {
    this.pKey = pKey
    this.ref = React.createRef()

    this.editorId = `editor_${info.attrs?.key}`

    this.inputAttrs = {
      ref: this.ref,
      defaultValue: options.default,
      ...options.options,
    }

    if (options.readOnly === true) {
      this.inputAttrs.disabled = true
    }
  }

  setValue(value: string): void {
    if (value !== undefined) {
      if (this.ref.current != null) {
        this.ref.current.setContent(value)
        const editorBody = this.ref.current.getBody()
        if (editorBody != null) {
          const text = editorBody.innerText
          if (text.indexOf('\n') < 0 && text.trim().length === 0) {
            this.ref.current.setContent('<br/>')
          }
        }
      } else {
        this.inputAttrs.defaultValue = value
      }
    }
  }

  getValue(): string {
    if (this.ref.current != null) {
      const content = this.ref.current.getContent().trim()
      return content
    }
    return this.inputAttrs.defaultValue
  }

  getEditorConfig(reactObj: ReactObjBase): any {
    if (this.editorConfig == null) {
      this.editorConfig = {
        body_class: 'tinymce-editor',
        forced_root_block: 'DIV',
        content_style: 'body { overflow-y: auto !important; }',
        height: 300,
        menubar: false,
        resize: true,
        statusbar: true,
        plugins: 'paste autolink directionality autoresize code',
        paste_webkit_styles: 'color font-size font-weight',
        default_link_target: '_blank',
        autoresize_bottom_margin: 0,
        autoresize_overflow_padding: 0,
        auto_focus: this.editorId,
        toolbar_persist: true,
        toolbar: [
          {
            name: 'history',
            items: ['undo', 'redo'],
          },
          {
            name: 'formatting',
            items: ['bold', 'italic', 'underline', 'strikethrough', 'removeformat'],
          },
          {
            name: 'code',
            items: ['code'],
          },
        ],
        setup: (editor: any) => {
          editor.on('NodeChange', (evt: any) => {
            console.log(' ---- NodeChange ---- ', evt)

            if (evt.selectionChange === true || editor.selection.isCollapsed() === false) {
              const rng = editor.selection.getRng()
              const { startContainer, endContainer } = rng

              const body = editor.getBody()
              if (body.contains(startContainer) !== true || body.contains(endContainer) !== true) {
                console.log(' ---- outer range selected~!')
                editor.selection.select(editor.getBody(), true)
                editor.selection.collapse(false)
                editor.focus()
              }
            }
          })

          const processPaste = (node: HTMLElement) => {
            console.log(' --- processPaste - ', node)
            const pNode = node
            const mceNodes = pNode.querySelectorAll('[data-mce-style]')
            for (let i = mceNodes.length - 1; i >= 0; i -= 1) {
              mceNodes[i].removeAttribute('data-mce-style')
            }
            const imgs = pNode.querySelectorAll('img')
            for (let i = imgs.length - 1; i >= 0; i -= 1) {
              imgs[i].parentElement?.removeChild(imgs[i])
            }
            const nodes = pNode.querySelectorAll('[class]')
            for (let i = nodes.length - 1; i >= 0; i -= 1) {
              nodes[i].removeAttribute('class')
            }
            const styleNodes = pNode.querySelectorAll('[style]') as NodeListOf<HTMLElement>
            for (let i = styleNodes.length - 1; i >= 0; i -= 1) {
              styleNodes[i].style.background = ''
              styleNodes[i].style.color = ''
            }
          }

          editor.on('PastePostProcess', (e: any) => {
            processPaste(e.node)
          })

          const preprocessNode = (node: HTMLElement) => {
            console.log(' --- preprocessNode - ', node)
            const pNode = node
            const mceNodes = pNode.querySelectorAll('[data-mce-style]')
            for (let i = mceNodes.length - 1; i >= 0; i -= 1) {
              mceNodes[i].removeAttribute('data-mce-style')
            }
          }

          editor.on('PreProcess', (e: any) => {
            preprocessNode(e.node)
          })

          editor.on('ScrollIntoView', (e: any) => {
            console.log(' --- ScrollIntoView --- ', e)
            const bodyElem = editor.getBody()
            if (bodyElem != null && bodyElem.scrollHeight > bodyElem.offsetHeight) {
              const node = editor.selection.getNode()
              const bottomPos = e.elm.offsetTop + e.elm.offsetHeight - bodyElem.scrollTop
              const bodyHeight = bodyElem.offsetHeight
              console.log(' --- ScrollIntoView --- ', e, node, bottomPos, bodyHeight)
              if (bottomPos > bodyHeight) {
                bodyElem.scrollTop = bodyElem.scrollTop + bottomPos - bodyHeight
              }
            }
            this.prevHeight = bodyElem.offsetHeight
          })

          editor.on('focus', () => {
            ;(window as any).tinymce.$(editor.getBody().parentElement).addClass('focus')
          })

          editor.on('blur', () => {
            ;(window as any).tinymce.$(editor.getBody().parentElement).removeClass('focus')
          })

          editor.on('init', () => {
            const sel = editor.selection
            if (sel != null) {
              sel.select(editor.getBody(), true)
              sel.collapse(false)
            }
          })

          editor.on('input', (e: any) => {
            if (reactObj.onPropsChange != null) {
              reactObj.onPropsChange(this.pKey, e)
            }
          })

          editor.on('Change', (e: any) => {
            if (reactObj.onPropsChange != null) {
              reactObj.onPropsChange(this.pKey, e)
            }
          })
        },
      }
    }

    return this.editorConfig
  }

  getElement(reactObj: ReactObjBase): React.ReactElement {
    const editorConfig = this.getEditorConfig(reactObj)

    const element = (
      <div className={classNames(styles0.editorContainer, 'editorContainer', this.editorId)}>
        <Editor
          id={this.editorId}
          tinymceScriptSrc={`${import.meta.env.BASE_URL}editor/tinymce_v5_10/tinymce.min.js`}
          onInit={(evt, editor) => {
            this.ref.current = editor
          }}
          initialValue={this.inputAttrs.defaultValue}
          init={editorConfig}
        />
      </div>
    )

    return element
  }
}
*/

interface InputWrapperOptions {
  default?: string | number
  readOnly?: boolean
  options?: Record<string, any>
  format?: string
}

class InputWrapper {
  pKey: string
  value: string | number
  inputAttrs: Record<string, any>
  ref?: React.RefObject<any>
  options: InputWrapperOptions

  constructor(
    pKey: string,
    info: ItemInfo,
    inputAttrs: Record<string, any>,
    options: InputWrapperOptions,
    styles: any,
    reactObj: ReactObjBase,
  ) {
    this.pKey = pKey
    this.value = options.default || ''
    this.options = options

    this.inputAttrs = {
      defaultValue: this.value,
      ...inputAttrs,
      ...options.options,
    }

    this.inputAttrs.onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (this.ref?.current != null) {
        this.value = this.ref.current.value
      }

      if (inputAttrs != null && inputAttrs.onChange != null) {
        inputAttrs.onChange(e)
      }
      if (options.options != null && options.options.onChange != null) {
        options.options.onChange(e)
      }
      if (reactObj.onPropsChange != null) {
        reactObj.onPropsChange(this.pKey, e)
      }
    }

    if (options.readOnly === true) {
      this.inputAttrs.disabled = true
    } else {
      this.ref = React.createRef()
      this.inputAttrs.ref = this.ref
    }
  }

  setValue(value: string | number): void {
    if (value !== undefined) {
      this.value = value
      if (this.ref != null && this.ref.current != null) {
        this.ref.current.value = value
      } else {
        this.inputAttrs.defaultValue = value
      }
    }
  }

  getValue(): string | number {
    if (this.ref != null && this.ref.current != null) {
      return this.ref.current.value
    }
    return this.value
  }

  getElement(): React.ReactElement {
    if (this.inputAttrs.disabled === true) {
      return <React.Fragment>{String(this.value)}</React.Fragment>
    } else {
      const lowerFormat = this.options.format != null ? this.options.format.toLowerCase() : null
      if (lowerFormat === 'number') {
        return <input type="number" {...this.inputAttrs} />
      } else if (lowerFormat === 'textarea') {
        return <textarea {...this.inputAttrs} />
      } else {
        return <input {...this.inputAttrs} />
      }
    }
  }
}

interface HiddenWrapperOptions {
  default?: any
}

class HiddenWrapper {
  pKey: string
  defaultValue: any
  value: any

  constructor(pKey: string, info: ItemInfo, options: HiddenWrapperOptions) {
    this.pKey = pKey
    this.defaultValue = options.default
    this.value = this.defaultValue
  }

  setValue(value: any): void {
    if (value !== undefined) {
      this.value = value
    }
  }

  getValue(): any {
    return this.value
  }

  getElement(): null {
    return null
  }
}

interface CustomWrapperOptions {
  default?: any
  useValue?: boolean | ((info: ItemInfo) => boolean)
  onSetValue?: (value: any, info: ItemInfo, wrapper: CustomWrapper) => void
  onGetValue?: (info: ItemInfo, wrapper: CustomWrapper) => void
  getElement?: (info: ItemInfo, wrapper: CustomWrapper) => React.ReactElement | any
}

class CustomWrapper {
  pKey: string
  styles: any
  reactObj: ReactObjBase
  defaultValue: any
  value: any
  isCustom: boolean
  useValue: boolean
  prevValue: any
  options: CustomWrapperOptions
  info: ItemInfo

  constructor(
    pKey: string,
    info: ItemInfo,
    options: CustomWrapperOptions,
    styles: any,
    reactObj: ReactObjBase,
  ) {
    this.pKey = pKey
    this.styles = styles
    this.reactObj = reactObj
    this.options = options
    this.info = info
    this.defaultValue = options.default
    this.value = this.defaultValue
    this.isCustom = true

    let useValue = false
    if (typeof options.useValue === 'function') {
      useValue = options.useValue(info)
    } else {
      useValue = options.useValue === true
    }

    this.useValue = useValue
    this.prevValue = this.value
  }

  notifyChange(value: any): void {
    if (this.useValue === true && this.prevValue !== value) {
      if (this.reactObj.onPropsChange != null) {
        this.reactObj.onPropsChange(this.pKey, value)
      }
      this.prevValue = value
    }
  }

  setValue(value: any): void {
    if (value !== undefined) {
      if (this.options.onSetValue != null) {
        this.options.onSetValue(value, this.info, this)
      }
      this.value = value
    }
  }

  getValue(): any {
    if (this.options.onGetValue != null) {
      this.options.onGetValue(this.info, this)
    }
    return this.value
  }

  getElement(): React.ReactElement | any {
    if (this.options.getElement != null) {
      return this.options.getElement(this.info, this)
    }
    return this.value
  }
}

interface ViewGroupOptions {
  title?: string
  titleKey?: string
  desc?: string
  descKey?: string
  key?: string
}

export class ViewGroup {
  viewInfo: Record<string, ItemInfo>
  itemKey?: string
  styles?: any
  childElems?: React.ReactNode[]
  element?: React.ReactElement
  options: ViewGroupOptions

  constructor(viewOptions: ViewGroupOptions) {
    this.options = viewOptions
    this.viewInfo = {}
  }

  setItemKey(pKey: string): void {
    this.itemKey = pKey
  }

  setStyles(styles: any): void {
    this.styles = styles
  }

  addItemInfo(pKey: string, info: ItemInfo): void {
    this.viewInfo[pKey] = info
  }

  getElement(): React.ReactElement {
    this.childElems = Object.entries(this.viewInfo).map(([, info]) => {
      return info.getElement()
    })

    const { title, titleKey, desc, descKey, key } = this.options

    /* i18next-extract-disable-next-line */
    const titleText = titleKey != null ? i18n.t(titleKey) : title

    /* i18next-extract-disable-next-line */
    const descText = descKey != null ? i18n.t(descKey) : desc

    const titleOptions = {
      title,
      titleKey,
      children: this.childElems,
      styles: this.styles,
      extra: null,
    }

    const pKey = key || this.itemKey

    this.element = (
      <Card key={pKey} title={titleText}>
        <div className={classNames('desc')}>{descText}</div>
        {getTitleElem(titleOptions)}
      </Card>
    )

    return this.element
  }
}

interface UIGroupOptions {
  title?: string
  titleKey?: string
  children: Record<string, any>
  styles?: any
  afterElems?: React.ReactNode
  reactObj: ReactObjBase
}

export class UIGroup {
  options: UIGroupOptions
  info: Record<string, ItemInfo>
  viewInfo: Record<string, ItemInfo>
  vGroupList: ViewGroup[]
  itemKey?: string
  childElems?: React.ReactNode[]
  vGroupElems?: React.ReactNode[]
  element?: React.ReactElement

  constructor(groupOptions: UIGroupOptions) {
    this.options = groupOptions
    this.info = {}
    this.viewInfo = {}
    this.vGroupList = []

    const { children, styles, reactObj } = groupOptions

    Object.entries(children).forEach(([key, opts]) => {
      if (this.info[key] == null) {
        this.info[key] = getItemInfo(this.info, key, opts, styles, reactObj)
        if (opts.vGroup != null) {
          if (this.vGroupList.indexOf(opts.vGroup) < 0) {
            opts.vGroup.setItemKey(key)
            opts.vGroup.setStyles(styles)
            this.vGroupList.push(opts.vGroup)
          }
          opts.vGroup.addItemInfo(key, this.info[key])
        } else {
          this.viewInfo[key] = this.info[key]
        }
      }
    })
  }

  setItemKey(key: string): void {
    this.itemKey = key
  }

  setValue(value: Record<string, any>, removeUsed?: boolean): void {
    Object.entries(this.info).forEach(([key, childInfo]) => {
      const newValue = value != null ? value[key] : null
      childInfo.wrapper.setValue(newValue)
      if (value != null && removeUsed === true) {
        delete value[key]
      }
    })
  }

  getValue(): Record<string, any> {
    const ret: Record<string, any> = {}
    Object.entries(this.info).forEach(([key, childInfo]) => {
      const val = childInfo.wrapper.getValue()
      const { isCustom, useValue } = childInfo.wrapper
      if ((isCustom !== true || useValue === true) && this.info[key].useCheck() === true) {
        ret[key] = val
      }
    })
    return ret
  }

  getElement(): React.ReactElement {
    this.childElems = Object.entries(this.viewInfo).map(([key]) => {
      return this.info[key].getElement()
    })

    this.vGroupElems = this.vGroupList.map((vGroup) => {
      return vGroup.getElement()
    })

    const { title, titleKey, styles, afterElems } = this.options

    const titleOptions = {
      title,
      titleKey,
      children: this.childElems.length > 0 ? this.childElems : null,
      styles,
      extra: null,
    }

    this.element = (
      <React.Fragment key={this.itemKey}>
        {getTitleElem(titleOptions)}
        {this.vGroupElems}
        {afterElems}
      </React.Fragment>
    )

    return this.element
  }
}

interface WrapperOptions {
  format?: string
  children?: Record<string, any>
  items?: Record<string, any>
  values?: Array<any>
  default?: any
  readOnly?: boolean
  options?: Record<string, any>
  [key: string]: any
}

function createWrapper(
  format: string | undefined,
  inputAttrs: Record<string, any>,
  options: WrapperOptions,
  info: ItemInfo,
  styles: any,
  pKey: string,
  reactObj: ReactObjBase,
): any {
  const lowerFormat = format != null ? format.toLowerCase() : null

  let ret: any = null

  switch (lowerFormat) {
    case 'props':
    case 'json':
      ret = new JSONWrapper(pKey, info, options.children || {}, options, styles, reactObj)
      break
    case 'array':
      ret = new ArrayWrapper(pKey, info, options.items || {}, options, styles, reactObj)
      break
    case 'select':
      ret = new SelectWrapper(
        pKey,
        info,
        inputAttrs,
        options as SelectWrapperOptions,
        styles,
        reactObj,
      )
      break
    case 'checkbox':
      ret = new CheckboxWrapper(
        pKey,
        info,
        inputAttrs,
        options as CheckboxWrapperOptions,
        styles,
        reactObj,
      )
      break
    case 'antd-select':
      ret = new AntdSelectWrapper(
        pKey,
        info,
        inputAttrs,
        options as AntdSelectWrapperOptions,
        styles,
        reactObj,
      )
      break
    case 'antd-switch':
      ret = new AntdSwitchWrapper(
        pKey,
        info,
        inputAttrs,
        options as AntdSwitchWrapperOptions,
        styles,
        reactObj,
      )
      break
    case 'antd-color':
      ret = new AntdColorWrapper(
        pKey,
        info,
        inputAttrs,
        options as AntdColorWrapperOptions,
        styles,
        reactObj,
      )
      break
    case 'antd-input':
      ret = new AntdInputWrapper(
        pKey,
        info,
        inputAttrs,
        options as AntdInputWrapperOptions,
        styles,
        reactObj,
      )
      break
    case 'antd-number':
      ret = new AntdNumberWrapper(
        pKey,
        info,
        inputAttrs,
        options as AntdNumberWrapperOptions,
        styles,
        reactObj,
      )
      break
    // [사용 안 함] 'html' 필드(HTMLEditorWrapper)는 현재 사용되지 않아 주석처리함.
    // 사용하게 되는 경우 ngwEditor를 적용하여 사용하도록 할 것.
    // case 'html':
    //   ret = new HTMLEditorWrapper(
    //     pKey,
    //     info,
    //     inputAttrs,
    //     options as HTMLEditorWrapperOptions,
    //     styles,
    //     reactObj,
    //   )
    //   break
    case 'hidden':
      ret = new HiddenWrapper(pKey, info, options as HiddenWrapperOptions)
      break
    case 'custom':
      ret = new CustomWrapper(pKey, info, options as CustomWrapperOptions, styles, reactObj)
      break
    case 'text':
    default:
      ret = new InputWrapper(
        pKey,
        info,
        inputAttrs,
        options as InputWrapperOptions,
        styles,
        reactObj,
      )
  }

  return ret
}

interface ItemOptions {
  label?: string | (() => string)
  labelKey?: string
  format?: string
  inputAttrs?: Record<string, any>
  attrs?: Record<string, any>
  hidden?: boolean
  use?: boolean | ((info: ItemInfo) => boolean)
  [key: string]: any
}

function getItemInfo(
  pInfo: Record<string, ItemInfo>,
  key: string,
  options: ItemOptions,
  styles: any,
  reactObj: ReactObjBase | null,
): ItemInfo {
  const { label, labelKey, format, inputAttrs, attrs } = options

  const info: ItemInfo = pInfo[key] || ({} as ItemInfo)

  if (attrs != null) {
    info.attrs = { ...(info.attrs || {}), ...attrs }
  }

  info.parent = pInfo

  info.makeLabel = () => {
    let labelObj = ''
    if (label != null) {
      if (typeof label === 'function') {
        labelObj = label()
      } else {
        labelObj = label
      }
    }

    // labelKey가 명시적으로 null이면 라벨을 표시하지 않음 (undefined와 구분)
    let labelText = null
    if (labelKey !== null) {
      /* i18next-extract-disable-next-line */
      labelText = (labelKey != null ? i18n.t(labelKey) : labelObj) || key
    }

    if (info.attrs == null) {
      info.attrs = {}
    }

    info.attrs.label = labelText
  }

  if (info.wrapper == null) {
    info.attrs = {
      ...(info.attrs || {}),
      key,
    }

    info.makeLabel()

    info.inputAttrs = {
      ...inputAttrs,
    }

    info.wrapper = createWrapper(format, info.inputAttrs, options, info, styles, key, reactObj!)
  }

  pInfo[key] = info

  const innerGetElement = () => {
    // Need to handle getElement method signatures properly
    let element: React.ReactElement | null = null

    if (info.wrapper instanceof SelectWrapper) {
      element = info.wrapper.getElement(options as SelectWrapperOptions)
    } else if (info.wrapper instanceof AntdSelectWrapper) {
      element = info.wrapper.getElement(options as AntdSelectWrapperOptions)
      // [사용 안 함] HTMLEditorWrapper 분기는 현재 사용되지 않아 주석처리함.
      // 사용하게 되는 경우 ngwEditor를 적용하여 사용하도록 할 것.
      // } else if (info.wrapper instanceof HTMLEditorWrapper) {
      //   element = info.wrapper.getElement(reactObj!)
    } else if (info.wrapper.getElement) {
      element = info.wrapper.getElement()
    }

    if (options.hidden !== true && element != null) {
      info.makeLabel()
      return <Descriptions.Item {...info.attrs}>{element}</Descriptions.Item>
    }

    return null
  }

  info.useCheck = () => {
    if (options.use != null) {
      let useVal = false
      if (typeof options.use === 'function') {
        useVal = options.use(info)
      } else {
        useVal = options.use === true
      }
      return useVal
    }
    return true
  }

  info.getElement = () => {
    if (info.useCheck() === true) {
      return innerGetElement()
    }
    return null
  }

  return info
}

export default UIGroup
